/**
 * Featured Article Slider
 * 自動切り替え機能付きスライダー
 */
class FeaturedArticleSlider {
    constructor(container) {
      if (!container) {
        throw new Error('Container element is required');
      }
  
      this.container = container;
      this.elements = this._getElements();
      this.state = this._getInitialState();
      this.eventHandlers = this._createEventHandlers();
      
      this._validateElements();
      this._init();
    }
  
    /**
     * DOM要素を取得
     */
    _getElements() {
      return {
        slider: this.container.querySelector('.featured-article__slider'),
        slides: this.container.querySelectorAll('.featured-article__slide'),
        dots: this.container.querySelectorAll('.featured-article__dot')
      };
    }
  
    /**
     * 初期状態を設定
     */
    _getInitialState() {
      return {
        currentSlide: 0,
        totalSlides: this.elements.slides.length,
        slideInterval: parseInt(this.container.dataset.slideInterval) || 3000,
        autoPlayTimer: null,
        isInitialized: false,
        isFocused: false
      };
    }
  
    /**
     * イベントハンドラーを作成
     */
    _createEventHandlers() {
      return {
        onDotClick: (index) => () => this.goToSlide(index),
        onFocusIn: () => this._handleFocusIn(),
        onFocusOut: () => this._handleFocusOut(),
        onKeyDown: (e) => this._handleKeyDown(e)
      };
    }
  
    /**
     * 要素の存在確認
     */
    _validateElements() {
      if (!this.elements.slider) {
        throw new Error('Slider element not found');
      }
      if (this.state.totalSlides === 0) {
        throw new Error('No slides found');
      }
    }
  
    /**
     * 初期化処理
     */
    _init() {
      if (this.state.totalSlides <= 1) {
        return; // スライドが1つ以下の場合は何もしない
      }
  
      this._initializeSlides();
      this._initializeDots();
      this._bindEvents();
      this._startAutoPlay();
      
      this.state.isInitialized = true;
    }
  
    /**
     * スライドの初期化
     */
    _initializeSlides() {
      this.elements.slides.forEach((slide, index) => {
        slide.classList.toggle('featured-article__slide--active', index === 0);
      });
    }
  
    /**
     * ドットの初期化
     */
    _initializeDots() {
      this.elements.dots.forEach((dot, index) => {
        dot.classList.toggle('featured-article__dot--active', index === 0);
      });
    }
  
    /**
     * イベントリスナーの設定
     */
    _bindEvents() {
      // ドットクリックイベント
      this.elements.dots.forEach((dot, index) => {
        dot.addEventListener('click', this.eventHandlers.onDotClick(index));
      });
  
      // アクセシビリティ対応：フォーカス管理
      this.container.addEventListener('focusin', this.eventHandlers.onFocusIn);
      this.container.addEventListener('focusout', this.eventHandlers.onFocusOut);
  
      // キーボードナビゲーション
      this.container.addEventListener('keydown', this.eventHandlers.onKeyDown);
    }
  
    /**
     * キーダウン時の処理
     */
    _handleKeyDown(e) {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.prevSlide();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.nextSlide();
          break;
      }
    }
  
    /**
     * 指定したスライドに移動
     */
    goToSlide(index) {
      if (!this._isValidSlideIndex(index) || index === this.state.currentSlide) {
        return;
      }
  
      this._updateSlideState(index);
      
      // ドットクリック時はフォーカスを外して自動スライドを再開
      if (document.activeElement && this.container.contains(document.activeElement)) {
        document.activeElement.blur();
      }
      
      this._startAutoPlay(); // タイマーをリセット
    }
  
    /**
     * 次のスライドに移動
     */
    nextSlide() {
      const nextIndex = (this.state.currentSlide + 1) % this.state.totalSlides;
      this.goToSlide(nextIndex);
    }
  
    /**
     * 前のスライドに移動
     */
    prevSlide() {
      const prevIndex = this.state.currentSlide === 0 
        ? this.state.totalSlides - 1 
        : this.state.currentSlide - 1;
      this.goToSlide(prevIndex);
    }
  
    /**
     * スライドインデックスの妥当性チェック
     */
    _isValidSlideIndex(index) {
      return index >= 0 && index < this.state.totalSlides;
    }
  
    /**
     * スライド状態の更新
     */
    _updateSlideState(newIndex) {
      // 現在のスライドを非アクティブにする
      this.elements.slides[this.state.currentSlide].classList.remove('featured-article__slide--active');
      this.elements.dots[this.state.currentSlide].classList.remove('featured-article__dot--active');
  
      // 新しいスライドをアクティブにする
      this.state.currentSlide = newIndex;
      this.elements.slides[this.state.currentSlide].classList.add('featured-article__slide--active');
      this.elements.dots[this.state.currentSlide].classList.add('featured-article__dot--active');
    }
  
    /**
     * 自動再生開始
     */
    _startAutoPlay() {
      this._stopAutoPlay(); // 多重タイマー防止
      
      // フォーカスが当たっている場合は自動再生しない
      if (this.state.isFocused) {
        return;
      }
  
      this.state.autoPlayTimer = setInterval(() => {
        this.nextSlide();
      }, this.state.slideInterval);
    }
  
    /**
     * 自動再生停止
     */
    _stopAutoPlay() {
      if (this.state.autoPlayTimer) {
        clearInterval(this.state.autoPlayTimer);
        this.state.autoPlayTimer = null;
      }
    }
  
    /**
     * フォーカスイン時の処理（アクセシビリティ対応）
     */
    _handleFocusIn() {
      this.state.isFocused = true;
      this._stopAutoPlay();
    }
  
    /**
     * フォーカスアウト時の処理（アクセシビリティ対応）
     */
    _handleFocusOut() {
      this.state.isFocused = false;
      this._startAutoPlay();
    }
  
    /**
     * インスタンスの破棄
     */
    destroy() {
      if (!this.state.isInitialized) {
        return;
      }
  
      this._stopAutoPlay();
      this._unbindEvents();
      this.state.isInitialized = false;
    }
  
    /**
     * イベントリスナーの削除
     */
    _unbindEvents() {
      // ドットクリックイベント
      this.elements.dots.forEach((dot, index) => {
        dot.removeEventListener('click', this.eventHandlers.onDotClick(index));
      });
  
      // フォーカス管理
      this.container.removeEventListener('focusin', this.eventHandlers.onFocusIn);
      this.container.removeEventListener('focusout', this.eventHandlers.onFocusOut);
  
      // キーボードナビゲーション
      this.container.removeEventListener('keydown', this.eventHandlers.onKeyDown);
    }
  }
  
  /**
   * スライダーの初期化を管理するクラス
   */
  class FeaturedArticleSliderManager {
    constructor() {
      this.instances = new Map();
      this._init();
    }
  
    /**
     * 初期化処理
     */
    _init() {
      this._bindShopifyEvents();
      this._initializeExistingSliders();
    }
  
    /**
     * Shopifyイベントの設定
     */
    _bindShopifyEvents() {
      // セクション読み込み時
      document.addEventListener('shopify:section:load', (event) => {
        this._handleSectionLoad(event);
      });
  
      // ブロック選択時
      document.addEventListener('shopify:block:select', (event) => {
        this._handleBlockSelect(event);
      });
    }
  
    /**
     * 既存スライダーの初期化
     */
    _initializeExistingSliders() {
      const containers = document.querySelectorAll('.featured-article__slider-container');
      containers.forEach(container => {
        this._createSliderInstance(container);
      });
    }
  
    /**
     * セクション読み込み時の処理
     */
    _handleSectionLoad(event) {
      const container = event.target.querySelector('.featured-article__slider-container');
      if (container) {
        this._createSliderInstance(container);
      }
    }
  
    /**
     * ブロック選択時の処理
     */
    _handleBlockSelect(event) {
      const container = event.target.querySelector('.featured-article__slider-container');
      if (container) {
        this._recreateSliderInstance(container);
      }
    }
  
    /**
     * スライダーインスタンスの作成
     */
    _createSliderInstance(container) {
      try {
        const instance = new FeaturedArticleSlider(container);
        this.instances.set(container, instance);
      } catch (error) {
        console.error('Featured Article Slider initialization failed:', error);
      }
    }
  
    /**
     * スライダーインスタンスの再作成
     */
    _recreateSliderInstance(container) {
      // 既存インスタンスを破棄
      const existingInstance = this.instances.get(container);
      if (existingInstance) {
        existingInstance.destroy();
        this.instances.delete(container);
      }
  
      // 新しいインスタンスを作成
      this._createSliderInstance(container);
    }
  }
  
  // DOMContentLoadedイベントでマネージャーを初期化
  document.addEventListener('DOMContentLoaded', () => {
    window.featuredArticleSliderManager = new FeaturedArticleSliderManager();
  }); 